import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import { useStyles } from "./styles";
import { Grid2, Typography, Stack, Box, Button } from "@mui/material";
import constants from "../../../constants/constants.json";
import BasicButton from "components/button";
import { useCallback, useEffect, useState } from "react";
import BasicTable from "components/table";
import SupplierModal from "components/supplierModal";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchField from "components/simpleSearch";
import ModalPopup from "components/modalPopup";
import {
  createSupplier,
  deleteSupplierById,
  editSupplier,
  getAllSuppliers,
  getRoles,
} from "./suppliers.services";
import { getToken } from "utils/loadAuth";
import { useAuth0 } from "@auth0/auth0-react";
import LoadingSpinner from "components/loadingSpinner";
import Notification from "components/snackbar";
import { useNavigate } from "react-router";

const Suppliers = ({ isNavbarOpen }) => {
  const theme = useTheme();
  const classes = useStyles({ theme, isNavbarOpen });
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedProducts, setSelectedProducts] = useState("");
  const [openSupplierModal, setOpenSupplierModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [filteredRows, setFilteredRows] = useState([]);
  const [copiedRows, setCopiedRows] = useState([]);
  const [roles, setRoles] = useState([]);

  const navigate = useNavigate();

  const getAllRoles = useCallback(async (token) => {
    try {
      const result = await getRoles({ token });
      if (result?.data) {
        setRoles(
          result.data?.map((role) => ({ id: role.id, Name: role.roleName })) ||
            []
        );
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.log("Error fetching roles:", error);
    }
  }, []);

  const getSuppliersData = useCallback(async () => {
    setIsLoading(true);
    const token = await getToken({
      getAccessTokenSilently,
      loginWithRedirect,
    });
    try {
      const result = await getAllSuppliers({ token });
      await getAllRoles(token);
      if (result?.data) {
        const rows = result?.data?.suppliers.map((supplier, index) => ({
          id: supplier.id,
          product:
            supplier.products
              .map((product) => product.product_name)
              .join(", ") || "-",
          supplier: supplier.name || "-",
          role: supplier?.role?.role_name || "-",
        }));

        setFilteredRows(rows);
        setCopiedRows(rows);
        setIsLoading(false);
        setAllProducts(result?.data?.products || []);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      if (error.status === 403) {
        navigate("/forbidden");
      } else if (error.status === 401) {
        navigate("/unauthorized");
      }
    }
  }, [getAccessTokenSilently, loginWithRedirect, navigate, getAllRoles]);

  useEffect(() => {
    getSuppliersData();
  }, [getSuppliersData]);

  const submitSupplier = useCallback(
    async (supplierName, selectedRole, selectedProducts = []) => {
      const formData = {
        id: isEdit ? selectedSupplier.id : undefined,
        name: supplierName,
        role_name: selectedRole,
        role_id: roles.find((role) => role.Name === selectedRole)?.id,
        product_names: selectedProducts.map((product) => product.title),
        product_ids: selectedProducts.map((product) => product.id),
        created_at: new Date().toUTCString(),
        updated_at: new Date().toUTCString(),
      };
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });
      let result;
      try {
        if (isEdit) {
          result = await editSupplier(token, formData);
          setMessage(constants.supplierEditSuccess);
        } else {
          result = await createSupplier(token, formData);
          setMessage(constants.supplierCreateSuccess);
        }
      } catch (error) {
        if (error?.status === 409) {
          setSnackbarOpen(true);
          setMessage(constants.duplicateSupplierError);
          setSeverity("error");
          setOpenSupplierModal(false);
          return;
        } else {
          setSnackbarOpen(true);
          setMessage(constants.errorMessage);
          setSeverity("error");
          setOpenSupplierModal(false);
          return;
        }
      }
      if (result?.status === 201 || result?.status === 200) {
        setSnackbarOpen(true);
        setSeverity("success");
      } else {
        setSnackbarOpen(true);
        setMessage(constants.errorMessage);
        setSeverity("error");
      }
      setOpenSupplierModal(false);
      getSuppliersData();
    },
    [
      getAccessTokenSilently,
      loginWithRedirect,
      roles,
      getSuppliersData,
      isEdit,
      selectedSupplier,
    ]
  );

  const getSelectedProductsForEdit = (productString) => {
    if (!productString) return [];
    const productNames = new Set(
      productString.split(", ").map((name) => name.trim())
    );
    return allProducts.filter((product) => productNames.has(product.title));
  };

  const getSelectedRolesForEdit = (selectedRole) => {
    if (!selectedRole) return [];
    const roleObj = roles.find((role) => selectedRole === role.Name);
    return roleObj || {};
  };

  const deleteHandler = useCallback(async () => {
    const token = await getToken({
      getAccessTokenSilently,
      loginWithRedirect,
    });
    const result = await deleteSupplierById(token, selectedSupplier.id);
    if (result?.status === 200) {
      getSuppliersData();
      setSnackbarOpen(true);
      setMessage(constants.supplierDeleteSuccess);
      setSeverity("success");
    } else {
      setSnackbarOpen(true);
      setMessage(constants.errorMessage);
      setSeverity("error");
    }
    setDeleteOpen(false);
  }, [
    selectedSupplier,
    getAccessTokenSilently,
    loginWithRedirect,
    getSuppliersData,
  ]);

  const columns = [
    {
      field: "supplier",
      headerName: "Supplier",
      flex: 0.5,
    },
    {
      field: "product",
      headerName: "Product(s)",
      flex: 1,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.5,
    },
    {
      field: "Actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.3,
      renderCell: (param) => {
        return (
          <Box
            data-testid={"action-container"}
            className={classes.actionsContainer}
          >
            <Button
              data-testid={"edit-btn"}
              className={classes.actionBtn}
              title={constants.edit}
              onClick={() => {
                setSelectedSupplier(param?.row);
                setSelectedRole(param?.row?.role);
                setSelectedProducts(param?.row?.product);
                setIsEdit(true);
                setOpenSupplierModal(true);
              }}
            >
              <EditIcon />
            </Button>

            <Button
              data-testid={"delete-btn"}
              className={classes.actionBtn}
              onClick={() => {
                setSelectedSupplier(param?.row);
                setDeleteOpen(true);
              }}
              title={constants.delete}
            >
              <DeleteIcon />
            </Button>
          </Box>
        );
      },
    },
  ];

  const onSearchChange = (value) => {
    setTableSearch(value);
    if (!value) {
      setFilteredRows(copiedRows);
      return;
    }
    const searchedRows = copiedRows?.filter(
      (row) =>
        row?.supplier?.toLowerCase().includes(value?.toLowerCase()) ||
        row?.product?.toLowerCase().includes(value?.toLowerCase())
    );
    setFilteredRows(searchedRows);
  };

  return (
    <Grid2 container component="section" className={classes.wrapper}>
      <Stack className={classes.viewContainer} spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className={classes.titleWrapper}
        >
          <Typography
            data-testid="title"
            className={classes.title}
            variant="h5"
            align="center"
          >
            {constants.suppliers}
          </Typography>
          {!isLoading && (
            <div className={classes.actionWrapper}>
              <BasicButton
                title={constants.createSupplier}
                onClick={() => {
                  setIsEdit(false);
                  setOpenSupplierModal(true);
                }}
              />
            </div>
          )}
        </Stack>
        {isLoading ? (
          <Box id="loading" className={classes.loadingContainer}>
            <div id="loadingText">
              <LoadingSpinner loadingMessage={"Loading..."} />
            </div>
          </Box>
        ) : (
          <Grid2 container className={classes.pageWrapper}>
            <Stack className={classes.sectionsWrapper} spacing={1}>
              <Box sx={{ width: "100%" }}>
                <SearchField
                  searchValue={tableSearch}
                  setSearchValue={(value) => onSearchChange(value)}
                  placeholderText={constants.searchSupplierLabel}
                />
                <Box className={classes.tableWrapper}>
                  <BasicTable
                    disableColumnMenu
                    showBorder={false}
                    rows={filteredRows}
                    columns={columns}
                  />
                </Box>
              </Box>
            </Stack>
          </Grid2>
        )}
      </Stack>
      <SupplierModal
        roles={roles}
        products={allProducts}
        handleClose={() => setOpenSupplierModal(false)}
        title={constants.createSupplier}
        open={openSupplierModal}
        primaryBtnText={constants.cancel}
        secondaryBtnAction={submitSupplier}
        secondaryButtonText={constants.save}
        role={isEdit ? getSelectedRolesForEdit(selectedRole) : {}}
        supplier={isEdit ? selectedSupplier : {}}
        assignedProducts={
          isEdit ? getSelectedProductsForEdit(selectedProducts) : []
        }
      />
      <ModalPopup
        title={constants.deleteConfirm}
        message={
          <Typography sx={{ textAlign: "center", lineHeight: "1.6" }}>
            <strong>{selectedSupplier?.supplier}</strong>{" "}
            {constants.deleteSupplierMsg}
          </Typography>
        }
        primaryBtnText="Yes"
        secondaryButtonText="No"
        primaryBtnAction={() => deleteHandler()}
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        secondarybtnAction={() => {
          setDeleteOpen(false);
        }}
      />
      <Notification
        message={message}
        open={snackbarOpen}
        setOpen={setSnackbarOpen}
        severity={severity}
      />
    </Grid2>
  );
};

Suppliers.propTypes = {
  isNavbarOpen: PropTypes.bool,
};

export default Suppliers;
